import type { Email_Campaigns } from "@/src/gql/graphql";
import type { FieldValue } from "@/lib/types/table/shared";
import { EMAIL_CAMPAIGNS_CONFIG } from "@/features/email-campaigns/components/EmailCampaignsConfig";
import { EMAIL_CAMPAIGN_DETAIL_CONFIG } from "@/features/email-campaigns/components/EmailCampaignDetailConfig";
import { useEmailCampaignsConfigEnhancer } from "@/lib/hooks/api/shared/config-enhancers";
import { getFieldValue } from "@/components/shared/UniversalCRMTable/components/SimpleTable/tableCellFieldValue";
import { defineEntity } from "../types";

// ============================================================================
// EMAIL CAMPAIGNS ENTITY DEFINITION
// ============================================================================
// First-class Hub entity modeled on `social_media_posts`. Read-mostly in the
// CRM: campaigns are authored/sent by the marketing send pipeline
// (/api/cron/email-campaign-send), not the generic create form.
// Column change: edit features/email-campaigns/components/EmailCampaignsConfig.ts.
// Detail change: edit features/email-campaigns/components/EmailCampaignDetailConfig.ts.
//
// The engagement columns (recipients_count / sent_count / opened_count /
// clicked_count) are SYNTHETIC — they don't exist as columns; they project the
// campaign's `stats` jsonb counters below.
// ============================================================================

/** Keys in the `stats` jsonb that back the synthetic engagement columns. */
const STATS_COUNT_FIELDS: Record<string, string> = {
  recipients_count: "recipients",
  sent_count: "sent",
  opened_count: "opened",
  clicked_count: "clicked",
};

/** Read a numeric counter out of the `stats` jsonb, defaulting to 0. */
function readStatCount(stats: unknown, statKey: string): number {
  if (stats === null || typeof stats !== "object") return 0;
  const value = (stats as Record<string, unknown>)[statKey];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export const emailCampaignsEntity = defineEntity<Email_Campaigns>({
  tableConfig: EMAIL_CAMPAIGNS_CONFIG,
  detailConfig: EMAIL_CAMPAIGN_DETAIL_CONFIG,

  synthesizeField(item, fieldId): FieldValue | Record<string, unknown> | undefined {
    const statKey = STATS_COUNT_FIELDS[fieldId];
    if (statKey) {
      return readStatCount(item.stats, statKey);
    }
    return getFieldValue(item, fieldId);
  },

  // Dynamic brand options — see the note on the social-posts entity.
  useConfigEnhancer: (config) => useEmailCampaignsConfigEnhancer({ config }),
});
